import { useEffect, useState } from 'react';

// Long enough to not ask on every keystroke, short enough to feel live.
const DEBOUNCE_MS = 500;

/** Resolves whether `value` is still free, `null` if the check could not run. */
export type AvailabilityCheckFn = (value: string) => Promise<boolean | null>;

export interface AvailabilityCheck {
  isChecking: boolean;
  /** `null` = not checked (empty or unfinished value, or the check failed). */
  isAvailable: boolean | null;
}

/**
 * Debounced live check of a form value. `value` is the normalized value to check, or `''` while it is not checkable yet.
 * A result only counts for exactly the value it was made for – typing on makes it stale at once.
 */
export const useAvailabilityCheck = (value: string, check: AvailabilityCheckFn | undefined): AvailabilityCheck => {
  const [result, setResult] = useState<{ value: string; isAvailable: boolean | null } | null>(null);

  useEffect(() => {
    if (!check || !value) return;
    let isCurrent = true;
    const timer = setTimeout(async () => {
      const isAvailable = await check(value);
      if (isCurrent) setResult({ value, isAvailable });
    }, DEBOUNCE_MS);
    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [value, check]);

  const isCurrentResult = result?.value === value;
  return {
    isChecking: !!check && !!value && !isCurrentResult,
    isAvailable: isCurrentResult ? result.isAvailable : null
  };
};
